import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./TacticDemo.css";

/* Confetti after the goal: small rects raining from the TOP edge (like the match
   viewer), spread across the width, each drifting (--dx), falling (--dy) and
   spinning (--rot). Three staggered waves (w: a/b/c) so it doesn't drop at once. */
const CONFETTI = [
  { x: 12, y: 1, c: "var(--accent)", dx:  3, dy: 60, rot:  540, w: "a" },
  { x: 22, y: 3, c: "#f5c542",       dx: -4, dy: 58, rot: -420, w: "b" },
  { x: 30, y: 0, c: "#22c55e",       dx:  5, dy: 62, rot:  360, w: "c" },
  { x: 38, y: 2, c: "#3b82f6",       dx: -2, dy: 57, rot: -600, w: "a" },
  { x: 46, y: 4, c: "#ef4444",       dx:  4, dy: 61, rot:  480, w: "b" },
  { x: 54, y: 1, c: "#f0f0f0",       dx: -5, dy: 59, rot: -360, w: "c" },
  { x: 62, y: 3, c: "#a855f7",       dx:  2, dy: 63, rot:  600, w: "a" },
  { x: 70, y: 0, c: "var(--accent)", dx: -3, dy: 56, rot: -540, w: "b" },
  { x: 78, y: 2, c: "#f5c542",       dx:  6, dy: 60, rot:  420, w: "c" },
  { x: 86, y: 4, c: "#22c55e",       dx: -4, dy: 58, rot: -480, w: "a" },
  { x: 18, y: 2, c: "#3b82f6",       dx:  3, dy: 62, rot:  360, w: "b" },
  { x: 34, y: 0, c: "#ef4444",       dx: -6, dy: 57, rot: -600, w: "c" },
  { x: 50, y: 3, c: "#f0f0f0",       dx:  5, dy: 61, rot:  540, w: "a" },
  { x: 66, y: 1, c: "#a855f7",       dx: -2, dy: 59, rot: -420, w: "b" },
  { x: 82, y: 4, c: "var(--accent)", dx:  4, dy: 63, rot:  480, w: "c" },
  { x: 26, y: 2, c: "#f5c542",       dx: -5, dy: 56, rot: -360, w: "a" },
  { x: 58, y: 0, c: "#22c55e",       dx:  2, dy: 60, rot:  600, w: "b" },
  { x: 92, y: 3, c: "#3b82f6",       dx: -3, dy: 58, rot: -540, w: "c" },
];

/**
 * Animated tactic demo for the landing ("Your players follow your rules").
 *
 * A real, localized nested tactic evaluated the way the rule inspector shows it,
 * in lockstep with a 3-beat pitch animation. Per beat the conditions are scanned
 * one at a time — accent while "checking", then green (true ✓) / red (false ✗) —
 * and the branch that wins lights its action in the inspector's amber (#f59e0b),
 * in sync with the player doing it on the pitch. He runs ONTO the ball and keeps
 * it at his feet (same coord) until the shot; a goal triggers ~3s of confetti.
 *
 *   Si (TengoPelota) {                          // false → beat 1 else
 *     Si (EstoyATiroDePortería) { Chuto }       // true+true → beat 3: shoots, scores
 *     Sino  { CorroAPorteríaRival }             // true+false → beat 2: carries to goal
 *   } Sino {
 *     CorroAPelota                              // false → beat 1: runs to the loose ball
 *   }
 *
 * Pure CSS/SVG on one shared clock (--td-dur) — no engine, no canvas. Every token
 * is real (backend/app/data/tokens); the colors mirror InspectorTactic.css.
 *
 * Guardrails: pauses off-view (IntersectionObserver → .td--running); freezes to a
 * static "shoot" frame under prefers-reduced-motion; fixed aspect-ratio → no CLS.
 * Kept self-contained/portable so it can later move to the hero.
 */
export default function TacticDemo() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [running, setRunning] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setRunning(entry.isIntersecting),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`td ${running ? "td--running" : ""}`}>
      {/* Pitch — decorative illustration; the code below carries the meaning. */}
      <svg className="td-pitch" viewBox="0 0 100 60" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
        <rect className="td-field" x="1" y="1" width="98" height="58" rx="3" />
        <line className="td-mark" x1="50" y1="1" x2="50" y2="59" />
        <circle className="td-mark" cx="50" cy="30" r="7" />
        <rect className="td-mark" x="82" y="16" width="17" height="28" />
        {/* goal + score flash */}
        <circle className="td-flash" cx="98" cy="30" r="11" />
        <rect className="td-goal" x="97" y="23" width="2.5" height="14" />
        {/* aim line (shown on the shot) */}
        <line className="td-aim" x1="76" y1="30" x2="97" y2="30" />
        {/* the player */}
        <circle className="td-player" cx="16" cy="30" r="4.2" />
        {/* the ball — drawn on top so it stays visible while glued to the player */}
        <circle className="td-ball" cx="50" cy="30" r="2.4" />
        {/* confetti (on top of everything) */}
        {CONFETTI.map((p, i) => (
          <rect
            key={i}
            className={`td-conf td-conf--${p.w}`}
            x={p.x}
            y={p.y}
            width="2.2"
            height="1.4"
            fill={p.c}
            style={{ "--dx": `${p.dx}px`, "--dy": `${p.dy}px`, "--rot": `${p.rot}deg` }}
          />
        ))}
      </svg>

      {/* Code — real tokens, nested Si/Sino, localized, one span per line. */}
      <pre className="td-code" aria-label="Tactic example">
        <span className="td-line td-cond1">
          <span className="td-kw">{t("start.tacticKwIf")}</span> <span className="td-paren">(</span><span className="td-query">{t("start.tacticHaveBall")}</span><span className="td-paren">)</span> <span className="td-brace">{"{"}</span><span className="td-mark"><span className="td-tick">✓</span><span className="td-cross">✗</span></span>
        </span>
        <span className="td-line td-cond2">
          {"  "}<span className="td-kw">{t("start.tacticKwIf")}</span> <span className="td-paren">(</span><span className="td-query">{t("start.tacticInRange")}</span><span className="td-paren">)</span> <span className="td-brace">{"{"}</span><span className="td-mark"><span className="td-tick">✓</span><span className="td-cross">✗</span></span>
        </span>
        <span className="td-line td-act3">{"    "}<span className="td-action">{t("start.tacticShoot")}</span></span>
        <span className="td-line">
          {"  "}<span className="td-brace">{"}"}</span> <span className="td-kw">{t("start.tacticKwElse")}</span> <span className="td-brace">{"{"}</span>
        </span>
        <span className="td-line td-act2">{"    "}<span className="td-action">{t("start.tacticRunToGoal")}</span></span>
        <span className="td-line">{"  "}<span className="td-brace">{"}"}</span></span>
        <span className="td-line">
          <span className="td-brace">{"}"}</span> <span className="td-kw">{t("start.tacticKwElse")}</span> <span className="td-brace">{"{"}</span>
        </span>
        <span className="td-line td-act1">{"  "}<span className="td-action">{t("start.tacticRunToBall")}</span></span>
        <span className="td-line"><span className="td-brace">{"}"}</span></span>
      </pre>
    </div>
  );
}
